import {
  memo,
  MouseEventHandler,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BillSummary } from '@enview/interface/types/bills/BillSummary';
import { useTranslation } from 'react-i18next';
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  SortingState,
  Updater,
  PaginationState,
} from '@tanstack/react-table';
import {
  Bill,
  BillAction,
  BillSponsor,
  Hearing,
} from '@enview/interface/types/bills/Bill';
import {
  getMostRecentAction,
  formatDate,
  convertIdToAbbreviatedTitle,
  getBillAlias,
} from '../../helpers/BillHelper';
import { IllinoisKeyActions } from '@enview/interface/utils/Language';
import moment from 'moment';
import TrackColumnField from '../Shared/BillTracking/TrackColumnField';
import BillTrackingButton from '../Shared/BillTracking/BillTrackingButton';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import BillBulkActionsDropdown from '../Shared/DropDowns/BillBulkActionsDropdown';
import BillViewDropdown from '../Shared/DropDowns/BillViewDropdown';
import { Spinner } from 'react-bootstrap';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import FeedbackPrompt from '../Shared/Prompts/FeedbackPrompt';
import { FeedbackType } from '@enview/interface/types/actions/FeedbackData';
import * as BillAnalytics from '../../analytics/BillAnalytics';
import useSearchId from '../../hooks/useSearchId';
import NoBillResults from './NoBillResults';
import BillTagButton from '../Shared/BillTagging/BillTagButton';
import BillPosition from '../Shared/BillPosition/BillPosition';
import WorkspacesColumnField from '../Shared/Tables/WorkspacesColumnField';
import WorkflowStatusColumnField from '../Shared/Tables/WorkflowStatusColumnField';
import AssigneeColumnField from '../Shared/Tables/AssigneeColumnField';
import PriorityColumnField from '../Shared/Tables/PriorityColumnField';
import { useDispatch, useSelector } from 'react-redux';
import {
  getJurisdictionsForTeamMode,
  getOrganizationUser,
  getOrgForTeamMode,
  getTeamMode,
  updateOrganizationUser,
} from '../../dux';
import { Link } from 'react-router-dom';
import {
  buildColumnOptions,
  democratPartyIdentifier,
  partyString,
  republicanPartyIdentifier,
  splitByDate,
} from '../../utils';
import {
  isIllinoisOnlySearch,
  TOO_MANY_RESULTS_THRESHOLD,
} from '../../helpers/BillSearchHelper';
import Info from '../../components/svg/InfoCircleIcon';
import BillStatusBadge from './BillStatusBadge';
import PaginationBar from '../Shared/Tables/PaginationBar';
import IndeterminateCheckbox from '../Shared/Tables/IndeterminateCheckbox';
import DraggableTableHeader from '../Shared/Tables/DraggableTableHeader';
import DragAlongCell from '../Shared/Tables/DragAlongCell';
import { SortByParam, SortOrder } from '../../models/SortOrder';
import { SORT_BY_FOR_COLUMN } from '../../utils/constants';
import { State } from '../../dux/@types';
import { DefaultBillTableColumns } from '@enview/interface/types/Bill';
import { skipToken } from '@reduxjs/toolkit/query';
import { useOrganizationCustomFields } from '../../api';
import BillCustomFieldValue from '../Shared/BillCustomField/BillCustomFieldValue';
import ColumnSelectDropdown from '../Shared/DropDowns/ColumnSelectDropdown';
import * as AccountAnalytics from '../../analytics/AccountAnalytics';
import _ from 'lodash-es';
import featureFlags, { FrontendFeature } from '../../featureFlags';

const MATCH_SCORE_COLUMN_ID = 'match-score';

type TabularBillSearchResultsProps = {
  params: any;
  searching: boolean;
  query: any;
  setQuery: (arg: any) => void;
  results:
    | {
        data: BillSummary[];
        count: number;
      }
    | undefined;
  isSuccess: boolean;
  onOpenSearchTips?: () => void;
};

const DEFAULT_PAGE_SIZE = 10;

//#region TabularBillSearchResults
export const TabularBillSearchResults = (
  props: TabularBillSearchResultsProps,
): ReactElement => {
  const { params, searching, query, setQuery, results, isSuccess, onOpenSearchTips } =
    props;
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const organization = useSelector(getOrgForTeamMode);
  const orgId = organization?.id;
  const orgJurisdictions = useSelector(getJurisdictionsForTeamMode);
  const canViewMatchScore = featureFlags.isFeatureEnabledForUser(
    FrontendFeature.MatchScoreColumn,
    orgUser,
  );
  const canViewWorkspaceColumns = featureFlags.isFeatureEnabledForUser(
    FrontendFeature.BillTriageWorkspaceColumns,
    orgUser,
  );
  const hasNewTrackingButton = featureFlags.isFeatureEnabledForUser(
    FrontendFeature.NewTrackingButton,
    orgUser,
  );

  // Keep showing the last successfully loaded results while a new fetch is in
  // flight (e.g. a page change), instead of blanking the table out.
  const [displayResults, setDisplayResults] = useState(results);
  useEffect(() => {
    if (!searching) setDisplayResults(results);
  }, [results, searching]);

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [tooltipAnchor, setTooltipAnchor] = useState('');
  const [tipContent, setTipContent] = useState<ReactElement<any>>(<></>);
  const [showTooltip, setShowTooltip] = useState(false);

  const { searchId } = useSearchId();

  //#region pagination
  const queryPage = Number(query.page ?? 1);
  const queryPageSize = Number(query.pageSize ?? DEFAULT_PAGE_SIZE);

  const pagination: PaginationState = useMemo(
    () => ({
      pageIndex: queryPage - 1,
      pageSize: queryPageSize,
    }),
    [query.page, query.pageSize],
  );

  const onPaginationChange = useCallback(
    (updater) => {
      // onPaginationChange expects updater function or value, same as setState
      const next = typeof updater === 'function' ? updater(pagination) : updater;

      const page = next.pageIndex + 1;
      const pageSize = next.pageSize;

      if (page === queryPage && pageSize === queryPageSize) return;

      BillAnalytics.trackPageChange({
        searchId,
        page,
        pageSize,
      });

      setQuery({ ...query, page, pageSize });
    },
    [queryPage, queryPageSize, query, setQuery, searchId],
  );

  //#region Column config
  const initialColumns =
    orgUser?.settings.billViewPreference?.views.billSearchView?.columns;
  const defaultColumns = Object.values(DefaultBillTableColumns);

  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    initialColumns ?? defaultColumns,
  );

  // Column order for DnD
  const [columnOrder, setColumnOrder] = useState<string[]>(() => [
    'select-col',
    ...(initialColumns ?? defaultColumns),
  ]);

  useEffect(() => {
    setColumnOrder((prev) => {
      // select column first
      const prevWithoutSelect = prev.filter((id) => id !== 'select-col');

      // keep only still-selected columns, in their current order
      const existing = prevWithoutSelect.filter((id) => selectedColumns.includes(id));

      // any newly selected columns that weren't in the order yet
      const added = selectedColumns.filter((id) => !existing.includes(id));

      const next = ['select-col', ...existing, ...added];
      if (next.length === prev.length && next.every((id, i) => id === prev[i]))
        return prev;
      return next;
    });
  }, [selectedColumns]);

  const columnOrderMounted = useRef(false);

  useEffect(() => {
    if (!columnOrderMounted.current) {
      columnOrderMounted.current = true;
      return;
    }

    if (!orgUser) return;

    const orderedColumns = columnOrder.filter((id) => id !== 'select-col');

    if (
      _.isEqual(
        orderedColumns,
        orgUser?.settings?.billViewPreference?.views?.billSearchView?.columns,
      )
    ) {
      return;
    }

    dispatch(
      updateOrganizationUser(
        {
          ...orgUser,
          firstName: orgUser.firstName,
          lastName: orgUser.lastName,
          email: orgUser.email,
          settings: {
            ...orgUser.settings,
            billViewPreference: {
              views: {
                billSearchView: {
                  columns: orderedColumns,
                  // keeping behavior from previous component
                  updatedAt: Date.now().toLocaleString(),
                },
              },
            },
          },
        },
        orgUser,
      ),
    );
    AccountAnalytics.trackBillViewColumnConfigChanged(
      orgUser.id,
      orderedColumns,
      'Bill Search',
    );
    dispatch(getOrganizationUser());
  }, [columnOrder]);

  const { data: customFields = [] } = useOrganizationCustomFields(
    orgId ? orgId : skipToken,
  );

  const isIllinoisSearch: boolean = useMemo(
    () => isIllinoisOnlySearch(params?.criteria?.jurisdictions),
    [params],
  );

  const columnOptions = useMemo(
    () =>
      buildColumnOptions(
        customFields,
        false,
        false,
        canViewMatchScore ? { [MATCH_SCORE_COLUMN_ID]: 'Match Score' } : {},
        isIllinoisSearch,
        canViewWorkspaceColumns,
      ),
    [customFields, canViewMatchScore, isIllinoisSearch, canViewWorkspaceColumns],
  );

  //#region sorting
  const [sorting, setSorting] = useState<SortingState>(() =>
    SortOrder.getSortingFromOrderParam(query.order),
  );

  useEffect(() => {
    setSorting(SortOrder.getSortingFromOrderParam(query.order));
  }, [query.order]);

  const customFieldTitles = useMemo(
    () => new Set(customFields.map((f) => f.title)),
    [customFields],
  );

  const handleSortChange = (updater: Updater<SortingState>) => {
    setSorting((prev) => {
      // Updater is either ColumnSort[] or a function: (prev: ColumnSort[]) => ColumnSort[]
      const next = typeof updater === 'function' ? updater(prev) : updater;

      const active = next[0];

      if (active) {
        // Check if sorting by a custom field column
        if (customFieldTitles.has(active.id)) {
          const orderParam = SortOrder.getCustomFieldSortParam(active.id, active.desc);
          setQuery({ order: orderParam, page: 1 });
          return next;
        }

        const sortBy = SORT_BY_FOR_COLUMN[active.id];
        if (sortBy) {
          const orderParam = new SortOrder(sortBy, active.desc).getQueryParam();
          if (orderParam) {
            setQuery({ order: orderParam, page: 1 });
          }
        }
      } else if (query.order !== SortByParam.LATEST) {
        setQuery({ order: SortByParam.LATEST, page: 1 });
      }

      return next;
    });
  };

  //#region Bill Table helpers
  const teamModeId = useSelector(getTeamMode);

  const sortActions = (a: BillAction, b: BillAction): number => {
    const dateDiff = moment.utc(b.actionDate).diff(moment.utc(a.actionDate));
    if (dateDiff === 0) {
      return b.order - a.order;
    }
    return dateDiff;
  };

  const getMostRecentKeyAction = (bill: BillSummary) => {
    const sortedActions = bill.actions ? [...bill.actions].sort(sortActions) : [];
    const latestKeyAction = sortedActions.find((action) =>
      IllinoisKeyActions.some((keyAction) => action.description.startsWith(keyAction)),
    );
    if (!latestKeyAction) return <></>;
    const actionDate = formatDate(latestKeyAction.actionDate);

    let chamber;
    if (latestKeyAction.chamber === 'upper') {
      chamber = 'Senate';
    } else if (latestKeyAction.chamber === 'lower') {
      chamber = 'House';
    }

    const keyActionId = 'latest-key-action' + bill.sourceBillId;

    const latestKeyActionContent = chamber
      ? `${actionDate} - ${chamber} - ${latestKeyAction.description}`
      : `${actionDate} - ${latestKeyAction.description}`;

    const handleKeyActionFocus = () => {
      setTipContent(<p>{latestKeyActionContent}</p>);
      setTooltipAnchor(keyActionId);
      setShowTooltip(true);
    };

    return (
      <p
        className="latest-key-action"
        id={keyActionId}
        onClick={handleKeyActionFocus}
        onMouseEnter={handleKeyActionFocus}
      >
        {latestKeyActionContent}
      </p>
    );
  };

  const showTableResults = displayResults && displayResults.count > 0;

  const handleTooltipContentClick: MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const tooltipContent = (
    primarySponsors: BillSponsor[],
    coSponsors: BillSponsor[],
  ) => {
    return (
      <div className="sponsors-tooltip" onClick={handleTooltipContentClick}>
        <span className="tooltip-header">Primary sponsors:</span>
        <ul>
          {primarySponsors.map((sponsor, index) => (
            <li key={`primary-${index}`}>
              {sponsor.person?.id ? (
                <Link to={`/person/${sponsor.person?.id}`}>
                  {`${sponsor.name}${partyString(sponsor)}`}
                </Link>
              ) : (
                `${sponsor.name}${partyString(sponsor)}`
              )}
            </li>
          ))}
        </ul>
        {!!coSponsors.length && (
          <>
            <span className="tooltip-header">Co-sponsors:</span>
            <ul>
              {coSponsors.map((sponsor, index) => (
                <li key={`co-${index}`}>
                  {sponsor.person?.id ? (
                    <Link to={`/person/${sponsor.person?.id}`}>
                      {`${sponsor.name}${partyString(sponsor)}`}
                    </Link>
                  ) : (
                    `${sponsor.name}${partyString(sponsor)}`
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    );
  };

  const renderBillSponsors = (bill: Bill) => {
    const { sponsors } = bill;
    if (!sponsors) return null;

    const currentSponsors = sponsors.filter((s) => !s.isRemoved);
    const primarySponsors = currentSponsors.filter((s) => s.isPrimary);
    const coSponsors = currentSponsors.filter((s) => !s.isPrimary && !s.isRemoved);

    if (!primarySponsors.length) return null;

    const tooltipId = `table-tooltip-${bill.id}`;

    const handleSponsorFocus = () => {
      setTipContent(tooltipContent(primarySponsors, coSponsors));
      setTooltipAnchor(tooltipId);
      setShowTooltip(true);
    };

    return (
      <p
        className="bill-sponsor-table-cell"
        id={tooltipId}
        key={bill.id}
        onClick={handleSponsorFocus}
        onMouseEnter={handleSponsorFocus}
      >
        <span>{primarySponsors[0].name + partyString(primarySponsors[0])}</span>
        <Info className="ml-1" color="#4a4a4a" size={16} />
      </p>
    );
  };

  const renderLatestCommitteeHearing = (bill: BillSummary) => {
    const { hearings } = bill;
    if (!hearings || hearings.length === 0) return null;

    const { pastEvents } = splitByDate(hearings);
    if (pastEvents.length === 0) return null;

    const latestPast = pastEvents.reduce((latest, curr) =>
      new Date(curr.start).getTime() > new Date(latest.start).getTime() ? curr : latest,
    );

    const hearingDate = formatDate(latestPast.start, 'short');
    const hearingId = 'latest-committee-hearing-' + bill.sourceBillId;
    const hearingContent = `${hearingDate} - ${latestPast.eventData.name}`;

    const handleFocus = () => {
      setTipContent(<p>{hearingContent}</p>);
      setTooltipAnchor(hearingId);
      setShowTooltip(true);
    };

    return (
      <span id={hearingId} onClick={handleFocus} onMouseEnter={handleFocus}>
        {hearingContent}
      </span>
    );
  };

  const renderUpcomingHearings = (bill: BillSummary) => {
    const { hearings } = bill;
    if (!hearings || hearings.length === 0) return null;

    const { upcomingEvents, pastEvents } = splitByDate(hearings);
    let displayedEvent: Hearing;

    if (upcomingEvents.length > 0) {
      displayedEvent = upcomingEvents.reduce((nearest, curr) =>
        new Date(curr.start).getTime() < new Date(nearest.start).getTime()
          ? curr
          : nearest,
      );
    } else {
      displayedEvent = pastEvents.reduce((latest, curr) =>
        new Date(curr.start).getTime() > new Date(latest.start).getTime()
          ? curr
          : latest,
      );
    }

    const hearingDate = formatDate(displayedEvent.start, 'short');
    const upcomingHearingsId = 'upcoming-hearing' + bill.sourceBillId;
    const upcomingEventContent = `${hearingDate} - ${displayedEvent.eventData.name}`;

    const handleUpcomingHearingFocus = () => {
      setTipContent(<p>{upcomingEventContent}</p>);
      setTooltipAnchor(upcomingHearingsId);
      setShowTooltip(true);
    };

    return (
      <span
        id={upcomingHearingsId}
        onClick={handleUpcomingHearingFocus}
        onMouseEnter={handleUpcomingHearingFocus}
      >
        {upcomingEventContent}
      </span>
    );
  };

  const MemoTagButton = memo(BillTagButton);

  const handleSearchFeedback = (isPositve: boolean): void => {
    BillAnalytics.trackAISearchFeedback(
      searchId,
      isPositve,
      query.page,
      query.pageSize,
      displayResults?.count,
    );
  };

  //#region Columns
  const columns = useMemo<ColumnDef<BillSummary>[]>(() => {
    // Selection column is always present and not part of column config
    const selectionColumn: ColumnDef<BillSummary> = {
      id: 'select-col',
      size: 40,
      enableSorting: false,
      header: ({ table }) => {
        return (
          <IndeterminateCheckbox
            aria-label="Select all rows on this page"
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
          />
        );
      },
      cell: ({ row }) => (
        <IndeterminateCheckbox
          aria-label="Select row"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
    };

    const baseColumns: ColumnDef<BillSummary>[] = [
      {
        header: 'Tracking',
        id: DefaultBillTableColumns.TRACKING,
        size: 150,
        enableSorting: false,
        minSize: 110,
        cell: ({ row }) =>
          hasNewTrackingButton ? (
            <TrackColumnField bill={row.original as Bill} />
          ) : (
            <BillTrackingButton
              bill={row.original as Bill}
              hasStatusIndicator
              isCompactMode
            />
          ),
      },
      {
        id: DefaultBillTableColumns.TAGS,
        header: 'Tags',
        size: 50,
        accessorKey: 'tags',
        enableSorting: false,
        meta: { align: 'center' },
        cell: ({ row }) => (
          <MemoTagButton bill={row.original as Bill} hasStatusIndicator />
        ),
      },
      {
        header: 'Position',
        id: DefaultBillTableColumns.POSITION,
        size: 50,
        cell: ({ row }) => <BillPosition bill={row.original as Bill} />,
      },
      {
        header: 'Bill ID',
        id: DefaultBillTableColumns.BILL_ID,
        accessorKey: 'id',
        enableSorting: true,
        maxSize: 200,
        cell({ row }) {
          const bill = row.original;
          const title = convertIdToAbbreviatedTitle(bill.id);
          const billAlias = getBillAlias(bill.userAliases, teamModeId);
          return (
            <Link
              className="bill-link"
              to={`/legislative-tracking/bill/details/${bill.id}`}
            >
              {title}
              {billAlias ? `(${billAlias.alias})` : ''}
            </Link>
          );
        },
      },
      {
        header: 'Bill ID (Source)',
        id: DefaultBillTableColumns.SOURCE_LINK,
        enableSorting: false,
        maxSize: 200,
        cell({ row }) {
          const bill = row.original;
          const title = convertIdToAbbreviatedTitle(bill.id);
          if (!bill.sourceUrl) return <span>{title}</span>;
          return (
            <a href={bill.sourceUrl} rel="noreferrer" target="_blank">
              {title}
            </a>
          );
        },
      },
      {
        header: 'Bill Title',
        id: DefaultBillTableColumns.BILL_TITLE,
        accessorKey: 'name',
        enableSorting: true,
        minSize: 200,
        // TODO: Enable resizing of row width; clamp bill titles while allowing the row height and text shown to grow based on other cells.”
        // enableResizing: true,
        cell: ({ row }) => (
          <span className="bill-text-truncated">{row.original.name}</span>
        ),
      },
      {
        header: 'Bill Sponsors',
        id: DefaultBillTableColumns.BILL_SPONSORS,
        enableSorting: false,
        minSize: 150,
        maxSize: 300,
        cell: ({ row }) => renderBillSponsors(row.original as Bill),
      },
      {
        header: '(D)',
        id: DefaultBillTableColumns.DEMOCRATS,
        accessorKey: 'democrats',
        enableSorting: false,
        minSize: 50,
        maxSize: 75,
        cell: ({ row }) => {
          const { sponsors } = row.original;
          if (!sponsors) return undefined;
          const count = sponsors.filter(
            (sponsor) => partyString(sponsor) === democratPartyIdentifier,
          ).length;
          return <p className="text-center">{count}</p>;
        },
      },
      {
        header: '(R)',
        id: DefaultBillTableColumns.REPUBLICANS,
        accessorKey: 'republican',
        enableSorting: false,
        minSize: 50,
        maxSize: 75,
        cell: ({ row }) => {
          const { sponsors } = row.original;
          if (!sponsors) return undefined;
          const count = sponsors.filter(
            (sponsor) => partyString(sponsor) === republicanPartyIdentifier,
          ).length;
          return <p className="text-center">{count}</p>;
        },
      },
      {
        header: 'Bill Status',
        id: DefaultBillTableColumns.BILL_STATUS,
        accessorKey: 'status',
        enableSorting: true,
        minSize: 130,
        maxSize: 130,
        cell: ({ row }) => {
          return (
            <div>
              <BillStatusBadge bill={row.original as Bill} isCompactMode />
            </div>
          );
        },
      },
      {
        header: 'Jurisdiction',
        id: DefaultBillTableColumns.JURISDICTION,
        enableSorting: false,
        maxSize: 150,
        cell: ({ row }) => row.original.session.state.name,
      },
      {
        header: 'Session',
        id: DefaultBillTableColumns.SESSION,
        enableSorting: true,
        maxSize: 150,
        accessorKey: 'session.displayName',
      },
      {
        header: 'Latest Action',
        id: DefaultBillTableColumns.LATEST_ACTION,
        enableSorting: false,
        maxSize: 150,
        cell: ({ row }) => (
          <span className="bill-text-truncated">
            {getMostRecentAction(row.original)?.description}
          </span>
        ),
      },
      {
        header: 'Action Date',
        id: DefaultBillTableColumns.ACTION_DATE,
        enableSorting: true,
        accessorKey: 'action-date',
        minSize: 115,
        maxSize: 150,
        cell: ({ row }) => {
          const recentAction = getMostRecentAction(row.original);
          return recentAction ? formatDate(recentAction.actionDate, 'short') : '';
        },
      },
      {
        header: 'Upcoming Hearing',
        id: DefaultBillTableColumns.UPCOMING_HEARING,
        maxSize: 300,
        accessorKey: 'hearings',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="bill-text-truncated">
            {renderUpcomingHearings(row.original)}
          </span>
        ),
      },
      {
        header: 'Latest Committee Hearing',
        id: DefaultBillTableColumns.LATEST_COMMITTEE_HEARING,
        maxSize: 300,
        enableSorting: false,
        cell: ({ row }) => (
          <span className="bill-text-truncated">
            {renderLatestCommitteeHearing(row.original)}
          </span>
        ),
      },
      {
        header: 'Workspaces',
        id: DefaultBillTableColumns.WORKSPACES,
        enableSorting: false,
        minSize: 150,
        maxSize: 300,
        cell: ({ row }) => (
          <WorkspacesColumnField
            billId={row.original.id}
            billWorkspaces={row.original.workspaces}
          />
        ),
      },
      {
        header: 'Workflow Status',
        id: DefaultBillTableColumns.WORKFLOW_STATUS,
        enableSorting: false,
        minSize: 150,
        maxSize: 300,
        cell: ({ row }) => (
          <WorkflowStatusColumnField
            billId={row.original.id}
            billWorkspaces={row.original.workspaces}
            customFields={row.original.customFields}
          />
        ),
      },
      {
        header: 'Assignee',
        id: DefaultBillTableColumns.ASSIGNEE,
        enableSorting: false,
        minSize: 150,
        maxSize: 300,
        cell: ({ row }) => (
          <AssigneeColumnField
            billId={row.original.id}
            billWorkspaces={row.original.workspaces}
            customFields={row.original.customFields}
          />
        ),
      },
      {
        header: 'Priority',
        id: DefaultBillTableColumns.PRIORITY,
        enableSorting: false,
        minSize: 150,
        maxSize: 300,
        cell: ({ row }) => (
          <PriorityColumnField
            billId={row.original.id}
            billWorkspaces={row.original.workspaces}
            customFields={row.original.customFields}
          />
        ),
      },
    ];

    const latestKeyActionColumn: ColumnDef<BillSummary> = {
      header: 'Latest Key Action',
      id: DefaultBillTableColumns.LATEST_KEY_ACTION,
      maxSize: 200,
      cell: ({ row }) => (
        <span className="bill-text-truncated">
          {getMostRecentKeyAction(row.original)}
        </span>
      ),
    };

    if (isIllinoisSearch) {
      baseColumns.splice(12, 0, latestKeyActionColumn);
    }

    // Internal-only: Surfaces AI search relevance as a rank-percentile
    // confidence score. Not meaningful for keyword search, so left blank there.
    if (canViewMatchScore) {
      const matchScoreColumn: ColumnDef<BillSummary> = {
        header: 'Match Score',
        id: MATCH_SCORE_COLUMN_ID,
        enableSorting: false,
        minSize: 100,
        maxSize: 120,
        cell: ({ row }) => {
          if (!query.isAISearch || row.original.matchConfidence == null) return '';
          return <p className="text-center">{row.original.matchConfidence}%</p>;
        },
      };
      baseColumns.push(matchScoreColumn);
    }

    // Custom fields
    // System fields (Workflow Status, Priority, Assignee) have their own dedicated
    // columns so they are excluded here.
    // TODO: BillCustomFieldValue assumes every field's cell content is a rich-text
    // editor. That won't hold once system fields are rendered through this same path —
    // we need a sustainable way to render cell content per field type instead.

    const customColumns: ColumnDef<BillSummary>[] = orgId
      ? customFields
          .filter((field) => !field.isSystem)
          .map((field) => ({
            header: field.title,
            id: field.title,
            minSize: 250,
            enableSorting: false,
            // accessorKey: field.title,
            cell: ({ row }) => {
              const bill = row.original;
              const cfTooltipId = `cf-${field.id}-${bill.sourceBillId}`;
              return (
                <BillCustomFieldValue
                  bill={bill}
                  field={field}
                  onHover={(content, anchorId) => {
                    setTipContent(content);
                    setTooltipAnchor(anchorId);
                    setShowTooltip(true);
                  }}
                  tooltipId={cfTooltipId}
                />
              );
            },
          }))
      : [];

    // Workspaces/Workflow Status/Assignee/Priority are still bill-triage-epic-only,
    // civic-eagle-staff-only features — Tracking is excluded from this gate since it's
    // an existing, already-GA'd control the epic only added a dropdown caret to, not
    // new functionality.
    const visibleBaseColumns = canViewWorkspaceColumns
      ? baseColumns
      : baseColumns.filter(
          (col) =>
            col.id !== DefaultBillTableColumns.WORKSPACES &&
            col.id !== DefaultBillTableColumns.WORKFLOW_STATUS &&
            col.id !== DefaultBillTableColumns.ASSIGNEE &&
            col.id !== DefaultBillTableColumns.PRIORITY,
        );

    const columnMap = [...visibleBaseColumns, ...customColumns].reduce<
      Record<string, ColumnDef<BillSummary>>
    >((acc, col) => {
      if (col.id) {
        acc[col.id] = col;
      }
      return acc;
    }, {});

    const visibleDataColumns = selectedColumns
      .map((id) => columnMap[id])
      .filter((col): col is ColumnDef<BillSummary> => Boolean(col));

    return [selectionColumn, ...visibleDataColumns];
  }, [
    isIllinoisSearch,
    customFields,
    selectedColumns,
    teamModeId,
    democratPartyIdentifier,
    republicanPartyIdentifier,
    orgId,
    canViewMatchScore,
    canViewWorkspaceColumns,
    query.isAISearch,
    hasNewTrackingButton,
  ]);

  //#region Tanstack table
  const table = useReactTable({
    data: displayResults?.data ?? [],
    columns,
    getRowId: (r) => r.id,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { rowSelection, pagination, sorting, columnOrder },
    // row selection
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    // pagination
    onPaginationChange: onPaginationChange,
    manualPagination: true,
    pageCount: Math.ceil((displayResults?.count ?? 0) / (pagination.pageSize || 1)),
    // sorting
    onSortingChange: handleSortChange,
    manualSorting: true,
    enableSorting: true,
    enableMultiSort: false,
    // DnD
    onColumnOrderChange: setColumnOrder,

    /* Uncomment to enable debug */
    // debugTable: true,
    // debugHeaders: true,
    // debugColumns: true,
  });

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder((colOrder) => {
        const oldIndex = colOrder.indexOf(active.id as string);
        const newIndex = colOrder.indexOf(over.id as string);
        return arrayMove(colOrder, oldIndex, newIndex);
      });
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  const selectedBills = table
    .getSelectedRowModel()
    .flatRows.map((r) => r.original as Bill);

  const showAIStartingPoint = !!query.isAISearch && !query.keywords;

  //#region UI
  return (
    <div className="list results-pane">
      <div className="result-count">
        <div className="result-total">
          <div className="result-dropdown-container">
            <div className="result-count-row">
              <div className="mb-2">{displayResults?.count ?? 0} Results</div>
              {!!query.isAISearch &&
                showTableResults &&
                (displayResults?.count ?? 0) >= TOO_MANY_RESULTS_THRESHOLD && (
                  <div className="too-many-results-tip">
                    <Info
                      className="too-many-results-tip__icon"
                      color="#f68c24"
                      size={14}
                    />
                    <strong>Too many results?</strong> Add more specificity to your
                    topic for more focused results.{' '}
                    <button
                      className="search-tips-link"
                      onClick={onOpenSearchTips}
                      type="button"
                    >
                      See search tips
                    </button>
                  </div>
                )}
            </div>
            <BillBulkActionsDropdown bills={selectedBills} />
          </div>
        </div>

        <div className="view-dropdown">
          {orgId && (
            <ColumnSelectDropdown
              className="mr-2"
              columnOptions={columnOptions}
              selectedColumns={selectedColumns}
              setSelectedColumns={setSelectedColumns}
            />
          )}
          <BillViewDropdown />
        </div>
      </div>

      {searching && !showTableResults && (
        <div className="table-spinner-overlay">
          <Spinner animation="border" role="status"></Spinner>
        </div>
      )}

      {showTableResults && (
        <div className="tanstack-table bill-table" id="bill-search-results-table">
          <ReactTooltip
            anchorId={tooltipAnchor}
            className="tooltip"
            clickable
            delayHide={100}
            isOpen={showTooltip}
            place="bottom"
            setIsOpen={setShowTooltip}
          >
            {tipContent}
          </ReactTooltip>

          <PaginationBar
            pagination={pagination}
            results={displayResults}
            table={table}
          />

          <div className="table-wrapper">
            <DndContext
              collisionDetection={closestCenter}
              modifiers={[restrictToHorizontalAxis]}
              onDragEnd={handleDragEnd}
              sensors={sensors}
            >
              <table>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      <SortableContext
                        items={columnOrder}
                        strategy={horizontalListSortingStrategy}
                      >
                        {headerGroup.headers.map((header) => (
                          <DraggableTableHeader header={header} key={header.id} />
                        ))}
                      </SortableContext>
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <SortableContext
                          items={columnOrder}
                          key={cell.id}
                          strategy={horizontalListSortingStrategy}
                        >
                          <DragAlongCell cell={cell} key={cell.id} />
                        </SortableContext>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </DndContext>
          </div>

          {!!query.isAISearch && !searching && !!displayResults?.data.length && (
            <FeedbackPrompt
              feedbackType={FeedbackType.BillAISearch}
              key={searchId}
              message={
                <span className="feedback-header-text">
                  {t('feedback.aiSearchHeaderText')}
                </span>
              }
              setFeedbackState={handleSearchFeedback}
              submitFeedbackMetadata={{
                count: displayResults.count,
                searchId,
              }}
            />
          )}
        </div>
      )}

      {!searching && (!results || results?.count == 0) && (
        <NoBillResults
          isAISearch={!!query.isAISearch}
          onStartingPointClick={(phrase) =>
            setQuery({
              ...query,
              keywords: phrase,
              jurisdictions: orgJurisdictions.map((j) => j.abbreviation),
              page: 1,
            })
          }
          showAIStartingPoint={showAIStartingPoint}
        />
      )}

      {!!query.isAISearch &&
        !searching &&
        isSuccess &&
        results?.count === 0 &&
        !showAIStartingPoint && (
          <FeedbackPrompt
            feedbackType={FeedbackType.BillAISearch}
            key={searchId}
            message={
              <span className="feedback-header-text">
                {t('feedback.aiSearchHeaderText')}
              </span>
            }
            setFeedbackState={handleSearchFeedback}
            submitFeedbackMetadata={{
              count: results.count,
              searchId,
            }}
          />
        )}
    </div>
  );
};

export default TabularBillSearchResults;
